<template>
  <div
    id="app-header"
    class="app-header"
  >
    <ContainerMain type="lg">
      <div class="app-header__inner">
        <!-- Logo -->
        <router-link
          id="app-header-logo"
          :to="localePath('/', locale)"
          class="app-header__logo"
        >
          <TalentLogoRri v-if="!isMobile" />
          <RockenLogo v-else />
        </router-link>

        <!-- Right side -->
        <div
          ref="headerRightRef"
          class="app-header__actions"
        >
          <!-- Auth buttons — desktop, logged-out only -->
          <template v-if="!user && !isMobile">
            <PrimeButton
              id="app-header-sign-up-btn"
              variant="link"
              :label="t('auth.sign_up')"
              @click="redirectToSignUp"
            />
            <PrimeButton
              id="app-header-sign-in-btn"
              variant="outlined"
              :label="t('auth.sign_in')"
              right-icon="arrow-right"
              @click="redirectToLogin"
            />
          </template>

          <!-- Avatar + hamburger — logged-in candidate only -->
          <template v-if="isCandidate">
            <ProfileAvatar
              id="app-header-avatar"
              class="app-header__avatar"
              :avatar="user.avatar"
              @click="router.push(localePath('/profile', locale))"
            />
            <PrimeButton
              id="app-header-menu-btn"
              variant="link"
              left-icon="menu"
              :icon-only="true"
              @click="toggleMenu"
            />
          </template>

          <!-- Hamburger — mobile, logged-out only -->
          <PrimeButton
            v-if="!user && isMobile"
            id="app-header-menu-btn"
            variant="link"
            left-icon="menu"
            :icon-only="true"
            @click="toggleMenu"
          />
        </div>
      </div>
    </ContainerMain>
  </div>

  <!-- Burger menu -->
  <Popover
    id="app-header-menu"
    ref="menuRef"
    class="app-header-menu"
    :append-to="headerRightRef ?? undefined"
  >
    <!-- Logged out (mobile only) -->
    <template v-if="!user">
      <div class="app-header-menu__auth-item">
        <PrimeButton
          id="app-header-menu-sign-in-btn"
          variant="outlined"
          :label="t('auth.sign_in')"
          :fluid="true"
          @click="redirectToLogin(); closeMenu()"
        />
      </div>
      <div class="app-header-menu__auth-item app-header-menu__auth-item--last">
        <PrimeButton
          id="app-header-menu-sign-up-btn"
          :label="t('auth.sign_up')"
          :fluid="true"
          @click="redirectToSignUp(); closeMenu()"
        />
      </div>
    </template>

    <!-- Logged in candidate -->
    <div
      v-else-if="isCandidate"
      class="app-header-menu__items"
    >
      <router-link
        id="app-header-menu-profile-link"
        :to="localePath('/profile', locale)"
        class="app-header-menu__item"
        @click="closeMenu"
      >
        <ProfileAvatar
          class="app-header-menu__item-avatar"
          :avatar="user.avatar"
        />
        <span class="app-header-menu__item-label app-header-menu__item-label--truncated">
          {{ user.name }}
        </span>
      </router-link>
      <router-link
        id="app-header-menu-settings-link"
        :to="localePath('/account', locale)"
        class="app-header-menu__item"
        @click="closeMenu"
      >
        <RriIcon
          icon="setting"
          size="md"
        />
        <span class="app-header-menu__item-label">
          {{ t('account.settings') }}
        </span>
      </router-link>
      <template v-if="isHelpPageEnabled">
        <div class="app-header-menu__divider" />
        <router-link
          id="app-header-menu-help-link"
          :to="localePath('/help', locale)"
          class="app-header-menu__item"
          @click="closeMenu"
        >
          <RriIcon
            icon="help-center"
            size="md"
          />
          <span class="app-header-menu__item-label">
            {{ t('help.title') }}
          </span>
        </router-link>
      </template>
      <div class="app-header-menu__divider" />
      <div
        id="app-header-menu-logout-btn"
        class="app-header-menu__item"
        @click="onLogout"
      >
        <RriIcon
          icon="exit"
          size="md"
        />
        <span class="app-header-menu__item-label">
          {{ t('auth.logout') }}
        </span>
      </div>
    </div>
  </Popover>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ROLE_CANDIDATE } from '@/constants/role.constants.js'

import ContainerMain from '@/components/common/ContainerMain.vue'
import RockenLogo from '@/components/icons/RockenLogo.vue'
import TalentLogoRri from '@/components/icons/TalentLogoRri.vue'
import PrimeButton from '@/components/button/PrimeButton.vue'
import RriIcon from '@/components/rri/RriIcon.vue'
import ProfileAvatar from '@/components/profile/ProfileAvatar.vue'
import Popover from 'primevue/popover'
import { localePath } from '@/utils/localePath.js'
import { useAuthStore } from '@/store/auth.js'
import { useFeaturesStore } from '@/v2/store/features'
import { useDevice } from '@/composables/useDevice.js'
import { MARKET_GERMANY, MARKET_SWITZERLAND } from '@/constants/market.constants'
import { appConfig } from '@/configs/appConfig'

const props = withDefaults(
  defineProps<{
    market?: string
  }>(),
  {
    market: MARKET_SWITZERLAND
  }
)

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()

const authStore = useAuthStore()
const featuresStore = useFeaturesStore()
const { isMobile } = useDevice()
const menuRef = ref()
const headerRightRef = ref<HTMLElement | null>(null)

const user = computed(() => authStore.getUser)
const isCandidate = computed(() => user.value?.activeRole === ROLE_CANDIDATE)
const isHelpPageEnabled = computed(() => featuresStore.isHelpPageEnabled)

// Resolve the help flag once we have an authenticated candidate, so the menu
// link reflects the user's flags without blocking initial render.
watch(
  isCandidate,
  (candidate) => {
    if (candidate) featuresStore.load()
  },
  { immediate: true }
)

function toggleMenu(event: MouseEvent) {
  menuRef.value?.toggle(event)
}

function closeMenu() {
  menuRef.value?.hide()
}

function redirectToLogin() {
  router.push({ path: '/login', query: { redirect: route.fullPath } })
}

function redirectToSignUp() {
  const url = props.market === MARKET_GERMANY ? appConfig.cmsUrlApplicationDe : appConfig.cmsUrlApplication
  window.open(url, '_blank')
}

async function onLogout() {
  closeMenu()
  await authStore.logout()
}
</script>

<style scoped lang="scss">
.app-header {
  padding: var(--space-20) 0 var(--space-12);
  background: var(--color-zinc-100);
}

.app-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 78px;
  padding: var(--space-12) var(--space-36);
  border: 1px solid var(--color-zinc-200);
  border-radius: var(--border-radius-24);
  background: var(--color-zinc-50);

  @include phone {
    padding-left: var(--space-24);
    padding-right: var(--space-24);
  }
}

.app-header__logo {
  display: flex;
}

.app-header__actions {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-8);
}

.app-header__avatar {
  flex-shrink: 0;
  width: 48px !important;
  height: 48px !important;
  cursor: pointer;
}
</style>

<style lang="scss">
.app-header-menu {
  &.p-popover {
    width: 270px;
    padding: 0;
    border-radius: var(--border-radius-16);
    border: 1px solid var(--color-zinc-200);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    left: auto !important;
    right: -12px !important;
    top: 100% !important;
    background: var(--color-zinc-50);
    color: var(--color-zinc-950);
  }

  &.p-popover::before,
  &.p-popover::after {
    display: none;
  }

  .p-popover-content {
    background: var(--color-zinc-50);
    color: var(--color-zinc-950);
    border-radius: var(--border-radius-16);
  }

  .app-header-menu__auth-item {
    padding: var(--space-4) var(--space-12) 0;

    &--last {
      padding: var(--space-8) var(--space-12) var(--space-4);
    }
  }

  .app-header-menu__items {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
  }

  .app-header-menu__item {
    display: flex;
    align-items: center;
    gap: var(--space-12);
    padding: var(--space-8);
    border-radius: var(--border-radius-12);
    color: var(--color-zinc-950);
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: rgb(var(--color-brand-700-rgb) / 10%);
    }
  }

  .app-header-menu__item-avatar {
    flex-shrink: 0;
    width: 24px !important;
    height: 24px !important;
  }

  .app-header-menu__item-label {
    font-size: var(--subtitle-2);
    font-weight: var(--weight-semibold);

    &--truncated {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .app-header-menu__divider {
    margin: var(--space-4) 0;
    border-top: 1px solid var(--color-zinc-200);
  }
}
</style>
